import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { storeService, CreateStoreDto } from "../../services/store.service";
import { useAuthStore } from "../../stores/authStore";
import { showToast } from "../../lib/toast";

export function CreateStorePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuthStore();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ phone?: string }>({});
  const [formData, setFormData] = useState<CreateStoreDto>({
    tenantId: user?.tenantId || "",
    name: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    phone: "",
    gstNumber: "",
    licenseNumber: "",
    licenseExpiry: null,
    status: "active",
  });

  useEffect(() => {
    if (isEditMode) {
      fetchStore();
    }
  }, [id]);

  const fetchStore = async () => {
    try {
      setLoading(true);
      const store = await storeService.getById(id!);
      setFormData({
        tenantId: store.tenantId || user?.tenantId || "",
        name: store.name || "",
        address: store.address || "",
        city: store.city || "",
        state: store.state || "",
        pincode: store.pincode || "",
        phone: store.phone || "",
        gstNumber: store.gstNumber || "",
        licenseNumber: store.licenseNumber || "",
        licenseExpiry: store.licenseExpiry
          ? new Date(store.licenseExpiry).toISOString().slice(0, 10)
          : null,
        status: store.status || "active",
      });
    } catch (error) {
      console.error("Failed to fetch store:", error);
      showToast.error("Failed to fetch store details");
      navigate("/stores");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    if (name === "phone") {
      setErrors((prev) => ({ ...prev, phone: undefined }));
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.tenantId) {
      showToast.error("Tenant is required");
      return;
    }
    if (!formData.name.trim()) {
      showToast.error("Store name is required");
      return;
    }
    if (!formData.address.trim()) {
      showToast.error("Address is required");
      return;
    }
    if (!formData.city.trim() || !formData.state.trim()) {
      showToast.error("City and State are required");
      return;
    }
    if (!formData.pincode.trim()) {
      showToast.error("Pincode is required");
      return;
    }
    if (!formData.phone.trim()) {
      showToast.error("Phone is required");
      return;
    }
    if (!/^[6-9]\d{9}$/.test(formData.phone.trim())) {
      setErrors({ phone: "Phone must be a valid Indian 10-digit number" });
      showToast.error("Invalid phone number");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...formData,
        licenseExpiry: formData.licenseExpiry || undefined,
      };
      if (isEditMode) {
        await storeService.update(id!, payload);
        showToast.success("Store updated successfully");
      } else {
        await storeService.create(payload);
        showToast.success("Store created successfully");
      }
      navigate("/stores");
    } catch (err: any) {
      const apiErrors: string[] = err.response?.data?.errors || [];
      const message = err.response?.data?.message;
      if (apiErrors.find((e) => e.toLowerCase().includes("phone"))) {
        setErrors({ phone: apiErrors.join(" ") });
      }
      showToast.error(
        apiErrors.length > 0
          ? apiErrors.join(" ")
          : message || `Failed to ${isEditMode ? "update" : "create"} store`,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate("/stores")}
          className="text-blue-600 hover:text-blue-800 mb-2"
        >
          ← Back to Stores
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? "Edit Store" : "Add New Store"}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Basic Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Store Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.phone
                    ? "border-red-300 focus:ring-red-200"
                    : "focus:ring-blue-500"
                }`}
              />
              {errors.phone && (
                <p className="text-xs text-red-600 mt-1">{errors.phone}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status || "active"}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Address</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address <span className="text-red-500">*</span>
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={2}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pincode <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Compliance */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Compliance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GST Number
              </label>
              <input
                type="text"
                name="gstNumber"
                value={formData.gstNumber || ""}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                License Number
              </label>
              <input
                type="text"
                name="licenseNumber"
                value={formData.licenseNumber || ""}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                License Expiry
              </label>
              <input
                type="date"
                name="licenseExpiry"
                value={formData.licenseExpiry || ""}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate("/stores")}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading
              ? "Saving..."
              : isEditMode
                ? "Update Store"
                : "Create Store"}
          </button>
        </div>
      </form>
    </div>
  );
}
